import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { hoursToMinutes } from '@/helpers';
import {
  DurationInput,
  DurationInputHandle,
} from '@/modules/common/components/molecules/inputs/duration-input';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { formatWeekdayWithShortDate, normalizeToWallTimeUTC } from '@/utils/date-formatters';

import { useCreateWorklog } from '../../hooks/use-time-track-mutations';

const MINUTES_PER_DAY = 24 * 60;

interface WeekDayQuickAddProps {
  /** Calendar date of the cell. Time of day will be set to "now" on submit. */
  date: Date;
  projectId: number;
  /**
   * Booked hours for this project on this day. When > 0 the duration input is
   * prefilled with this value on open so the user can log their booked time in
   * one click (OPS-1558). 0 / undefined opens the input empty.
   */
  bookedHours?: number;
  /** The trigger button rendered by the cell. */
  trigger: React.ReactNode;
}

/**
 * Inline quick-add popover for the week-view grid.
 *
 * Opens on `+` click. Numeric input accepts `2h`, `1.5`, `30m`, `2:30`, etc.
 * Description is optional. On submit, POSTs a worklog at the cell's date with
 * the current time.
 */
export function WeekDayQuickAdd({ date, projectId, bookedHours, trigger }: WeekDayQuickAddProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const inputRef = useRef<DurationInputHandle>(null);
  const [open, setOpen] = useState(false);
  const [minutesValue, setMinutesValue] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync, isPending } = useCreateWorklog();

  useEffect(() => {
    if (open) {
      // Prefill the duration with the day's booked hours so logging booked time
      // is a single click (OPS-1558). 0 / undefined leaves the input empty.
      setMinutesValue(
        bookedHours && bookedHours > 0 ? Math.round(hoursToMinutes(bookedHours)) : null
      );
      return;
    }
    setMinutesValue(null);
    setDescription('');
    setError(null);
  }, [open, bookedHours]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.select(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPending) return;

    // Parse the live text (covers the case where the user submits without blurring).
    const minutes = inputRef.current?.getMinutes() ?? minutesValue;
    if (!minutes) {
      setError(t('weekView.quickAdd.invalidInput'));
      inputRef.current?.focus();
      return;
    }
    if (minutes > MINUTES_PER_DAY) {
      setError(t('weekView.quickAdd.exceedsDay'));
      inputRef.current?.focus();
      return;
    }

    // Anchor the entry to "now", but shift the start time back when the
    // duration would otherwise spill past midnight — a worklog can't cross
    // a day boundary, so we clamp the end to 23:59 and back out the start.
    const now = new Date();
    const nowMinuteOfDay = now.getHours() * 60 + now.getMinutes();
    const startMinuteOfDay =
      nowMinuteOfDay + minutes > MINUTES_PER_DAY ? MINUTES_PER_DAY - minutes : nowMinuteOfDay;
    const startDate = new Date(date);
    startDate.setHours(Math.floor(startMinuteOfDay / 60), startMinuteOfDay % 60, 0, 0);

    try {
      await mutateAsync({
        projectId,
        startDate: normalizeToWallTimeUTC(startDate).toISOString(),
        trackedMinutes: minutes,
        description: description.trim(),
      });
      setOpen(false);
    } catch (err) {
      displayToastErrorFromApiWithFallback(
        err,
        'authenticated.timeTracking:api.error.timeTrackAddFailed'
      );
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        className="w-80 p-0"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 p-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('weekView.quickAdd.title')}
            </span>
            <span className="text-[12px] text-muted-foreground">
              {t('weekView.quickAdd.subtitle', {
                date: formatWeekdayWithShortDate(date),
              })}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <DurationInput
              ref={inputRef}
              value={minutesValue}
              onChange={value => {
                setMinutesValue(value);
                if (error) setError(null);
              }}
              error={error ?? t('weekView.quickAdd.invalidInput')}
              maxMinutes={MINUTES_PER_DAY}
              maxError={t('weekView.quickAdd.exceedsDay')}
              hint={t('weekView.quickAdd.hint')}
              placeholder={t('weekView.quickAdd.placeholder')}
              ariaLabel={t('weekView.quickAdd.inputAriaLabel')}
              className="flex-1"
            />
            <ButtonLoadable type="submit" isLoading={isPending} className="h-10 px-4">
              {t('weekView.quickAdd.addButton')}
            </ButtonLoadable>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="week-quick-add-description"
              className="text-[11px] font-medium text-muted-foreground"
            >
              {t('weekView.quickAdd.descriptionLabel')}
            </label>
            <Textarea
              id="week-quick-add-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('weekView.quickAdd.descriptionPlaceholder')}
              rows={2}
              className="max-h-[120px] min-h-[56px] resize-y text-[13px]"
            />
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
